/* MIRRORED FILE — an identical copy lives in the other project:
 *   game-client:      src/components/ui/use-modal-behavior.ts
 *   portal-frontend:  src/components/use-modal-behavior.ts
 * Keep byte-identical below this banner; apply any change to both copies in the
 * same commit. */

/**
 * Headless behaviour shared by every modal dialog: dismiss on Escape, lock
 * background scrolling, and keep keyboard focus inside the dialog.
 *
 * Deliberately headless — it returns a ref and nothing else, so it says nothing
 * about how a dialog looks. The client has several visual modal treatments and
 * consolidating those is a redesign question; this is only the behaviour, which
 * is the same for all of them and was previously either duplicated or missing.
 * Before this, Escape was hand-rolled in six components, scroll lock existed
 * nowhere, and no dialog trapped focus.
 *
 * Usage:
 *
 *     const ref = useModalBehavior<HTMLDivElement>({ isOpen: true, onDismiss: close });
 *     return <div ref={ref} role="dialog">…</div>;
 *
 * Only the topmost open dialog reacts to Escape, and scroll stays locked until
 * the last one closes, so stacked dialogs — a celebration over a summary —
 * behave the way a user expects. The topmost is the one drawn on top: the
 * highest rung (its z-index, read as it opens), and on one rung the latest. A
 * dialog that opens on a lower rung than one already open — a celebration
 * arriving under a notice drawn over every dialog — is drawn under it, so it
 * takes neither the focus nor the keys from it, until the one over it closes:
 * then it takes the focus, and hands it back, as it closes in turn, where the
 * one over it would have. One with no z-index of its own — a dock's level, a
 * flight's page or a route's list, drawn inside its dock — opens on the rung
 * on top, and so is the latest there. The order is one comparator, `drawnOnTop`, which the game
 * client's ui/modal-layers.ts reads too, to say which of the modals alone a
 * clock may run in. A layer that is inert, or inside something inert, takes
 * neither the keys nor the focus, however high it is drawn (`reachableOnTop`):
 * a list hanging from a dialog that is fading, drawn over the dialog under it,
 * stays up for the fade, and Escape went to it, closing what no one could
 * see; it passes over it to that dialog now. The stack knows a layer by the
 * element the ref is on, so every caller puts the ref on the layer it closes
 * — a caller that drops it is ranked by the rung on top, and never passed
 * over (the game client's design:check, modal-behavior-ref-dropped).
 *
 * As it opens it focuses its first control, or the one that asks for the focus
 * (`data-autofocus`): a gate whose words hold a quiet way on, before the
 * button under them that is the screen's one action, focuses the button.
 *
 * The element the ref is attached to may take the focus itself (focusItself):
 * as it opens with nothing focusable in it, and when the control holding the
 * focus is removed or disabled. It is a container, not a control, so its sheet
 * keeps the browser's ring off it (`:focus { outline: none }`); the next Tab
 * finds its first control.
 */

import { useEffect, useRef } from 'react';

/**
 * Focuses the dialog itself, where no control of its own holds the focus. It
 * needs a tabindex to be focusable, which callers should not have to remember.
 */
function focusItself(element: HTMLElement): void {
  element.tabIndex = -1;
  element.focus();
}

/** An open dialog: the rung it is drawn on, its element, and how it comes on top. */
interface OpenDialog {
  id: symbol;
  rung: number;
  /** What the hook's ref is on, which may be inert: then it takes neither the keys nor the focus. */
  element: HTMLElement | null;
  /** Whether it has come on top since it opened. */
  arrived: boolean;
  /**
   * It comes on top — as it opens, or as the one drawn over it closes — and
   * keeps `from` as where it hands the focus back; it takes the focus if it
   * moves it, and says whether it did.
   */
  arrive: (from: HTMLElement | null) => boolean;
}

/** Open dialogs, oldest first. */
const openDialogs: OpenDialog[] = [];

/**
 * Of open layers, oldest first, the one drawn on top: on the highest rung, and
 * of those the latest to open.
 */
export function drawnOnTop<T extends { rung: number }>(open: readonly T[]): T | undefined {
  let top: T | undefined;
  for (const layer of open) if (!top || layer.rung >= top.rung) top = layer;
  return top;
}

/** The rung a layer is drawn on: its element's z-index as it stands now, NaN where it sets none. */
export function rungOf(element: Element): number {
  return Number.parseInt(getComputedStyle(element).zIndex, 10);
}

/**
 * Of open layers, every one but the one drawn on top made inert, and that one
 * live: what keeps a modal under another out of reach. The game client's and
 * the portal's layer registries (modal-layers.ts in each) settle by it, so the
 * two cannot part.
 */
export function inertUnderTop<T extends { rung: number; element: { inert: boolean } | null }>(open: readonly T[]): void {
  const top = drawnOnTop(open);
  for (const layer of open) if (layer.element) layer.element.inert = layer !== top;
}

/**
 * Of open layers, oldest first, the one on top a player can reach: drawn on
 * top (drawnOnTop) of those that are not inert, nor inside something inert.
 */
export function reachableOnTop<T extends { rung: number; element: Pick<Element, 'closest'> | null }>(open: readonly T[]): T | undefined {
  return drawnOnTop(open.filter((layer) => !layer.element?.closest('[inert]')));
}

/** The open dialog on top that a player can reach. */
function topmost(): OpenDialog | undefined {
  return reachableOnTop(openDialogs);
}

/** Body scroll is restored only when the last dialog closes. */
let savedOverflow: string | null = null;

function lockScroll() {
  if (savedOverflow !== null) return;
  savedOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
}

function unlockScroll() {
  if (savedOverflow === null) return;
  document.body.style.overflow = savedOverflow;
  savedOverflow = null;
}

const FOCUSABLE = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Of a group of radios, Tab stops at one: the chosen, or the first while none
 * is. A trap that counted every radio took the group's last for the dialog's
 * last control, so Tab from a quiz's options went out of the dialog, and the
 * dialog took the focus back onto itself, where nothing showed it.
 */
function isTabStop(control: HTMLElement): boolean {
  if (!(control instanceof HTMLInputElement) || control.type !== 'radio' || !control.name) return true;
  const group = [...(control.form ?? control.ownerDocument).querySelectorAll<HTMLInputElement>('input[type="radio"]')].filter(
    (radio) => radio.name === control.name && radio.form === control.form,
  );
  return control === (group.find((radio) => radio.checked) ?? group[0]);
}

/**
 * Everything inside `root` that Tab stops at, in the page's order: what is
 * rendered, and whatever has focus now even if it is not — a fixed element has
 * no offsetParent — and of a group of radios the one Tab stops at. The game
 * client's ui/focusable.ts reads it too, for the layers there that move focus
 * themselves, so every layer counts the same stops.
 */
export function focusableWithin(root: ParentNode): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => (el.offsetParent !== null || el === document.activeElement) && isTabStop(el),
  );
}

export interface ModalBehaviorOptions {
  /** While false the hook does nothing, so it can be called unconditionally. */
  isOpen: boolean;
  /** Called when Escape is pressed, if this is the topmost dialog. */
  onDismiss?: () => void;
  closeOnEscape?: boolean;
  lockBackgroundScroll?: boolean;
  trapFocus?: boolean;
  /**
   * Take focus in as it opens — the control asking for it (`data-autofocus`),
   * else its first control, or itself — and hand it back to where it was as
   * it closes. On wherever focus is trapped; a panel that does not take the
   * page, and so traps nothing, asks for it alone, and hands focus back only
   * if it is still inside as the panel closes: where the player has taken it
   * since — the page stays live beside such a panel — it stays.
   */
  moveFocus?: boolean;
  /**
   * Where focus goes back to, rather than wherever it was as the dialog
   * opened — a popover's anchor. Given, focus goes back there from inside
   * whether or not it was moved in. It is read as the dialog closes, so it
   * can name a place that has changed since — a docked panel's opener,
   * followed through a panel that has gone.
   */
  returnFocusTo?: { readonly current: HTMLElement | null };
}

export function useModalBehavior<T extends HTMLElement>({
  isOpen,
  onDismiss,
  closeOnEscape = true,
  lockBackgroundScroll = true,
  trapFocus = true,
  moveFocus = trapFocus,
  returnFocusTo,
}: ModalBehaviorOptions) {
  const ref = useRef<T>(null);
  // Held in a ref so a caller passing an inline arrow does not re-run the main
  // effect on every render and thrash the dialog stack. Written from an effect
  // rather than during render, which React 19 disallows.
  const dismissRef = useRef(onDismiss);
  useEffect(() => {
    dismissRef.current = onDismiss;
  });

  useEffect(() => {
    if (!isOpen) return;

    const id = Symbol('dialog');
    const element = ref.current;
    const own = element ? rungOf(element) : Number.NaN;
    // Where focus goes back to as the dialog closes: `returnFocusTo` as it
    // stands then — the place may have changed since — else where it was as
    // the dialog came on top.
    let returnTo: HTMLElement | null = null;
    const backTo = () => returnFocusTo?.current ?? returnTo;
    const dialog: OpenDialog = {
      id,
      rung: Number.isNaN(own) ? (topmost()?.rung ?? 0) : own,
      element,
      arrived: false,
      arrive: (from) => {
        dialog.arrived = true;
        returnTo = from;
        if (!moveFocus || !element) return false;
        const controls = focusableWithin(element);
        const first = controls.find((control) => control.hasAttribute('data-autofocus')) ?? controls[0];
        // Fall back to the dialog itself so focus never stays behind it.
        if (first) first.focus();
        else focusItself(element);
        return true;
      },
    };
    openDialogs.push(dialog);
    const isTopmost = () => topmost()?.id === id;

    if (lockBackgroundScroll) lockScroll();

    // Focus moves in only to a dialog drawn on top: one opening under another
    // leaves the focus where the player can see it, until it comes on top.
    if (isTopmost()) dialog.arrive(document.activeElement as HTMLElement | null);

    // Whether focus is inside, kept as it moves, since a dialog that traps
    // nothing hands it back only from inside. A blur to nowhere is decided a
    // moment later: the browser blurs an element React is removing before it
    // leaves the page, and a dialog closing with focus inside has not lost it
    // to the player.
    let inside = element?.contains(document.activeElement) ?? false;
    let blurred = false;
    // A control that goes with the focus on it — removed, or disabled — drops
    // the focus to the page behind a dialog that traps it. The dialog drawn on
    // top takes it back onto itself, so the next Tab finds its first control
    // again. A blur to nowhere says so as it happens; a removal may say nothing
    // at all, so the dialog's own changes are watched as well.
    const reclaim = () => {
      if (!trapFocus || !element?.isConnected || !isTopmost()) return;
      const active = document.activeElement as HTMLElement | null;
      const lost = !active || active === document.body || (element.contains(active) && active.matches(':disabled'));
      if (!lost) return;
      focusItself(element);
    };
    const onFocusIn = () => {
      inside = true;
      blurred = false;
    };
    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget as Node | null;
      if (next) {
        inside = element?.contains(next) ?? false;
        return;
      }
      blurred = true;
      queueMicrotask(() => {
        if (blurred && element?.isConnected) inside = false;
        blurred = false;
        reclaim();
      });
    };
    element?.addEventListener('focusin', onFocusIn);
    element?.addEventListener('focusout', onFocusOut);
    const changes = new MutationObserver(reclaim);
    if (element && trapFocus) {
      changes.observe(element, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'hidden', 'inert'] });
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isTopmost()) return;

      if (closeOnEscape && event.key === 'Escape') {
        event.stopPropagation();
        dismissRef.current?.();
        return;
      }

      if (!trapFocus || event.key !== 'Tab' || !ref.current) return;
      const focusable = focusableWithin(ref.current);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !ref.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      element?.removeEventListener('focusin', onFocusIn);
      element?.removeEventListener('focusout', onFocusOut);
      changes.disconnect();
      const wasTopmost = isTopmost();
      const index = openDialogs.findIndex((open) => open.id === id);
      if (index !== -1) openDialogs.splice(index, 1);
      if (openDialogs.length === 0) unlockScroll();
      // A blur still undecided is decided now: the dialog still on the page
      // lost focus to nowhere; gone, it took focus with it.
      if (blurred && element?.isConnected) inside = false;
      const handBack = (moveFocus || returnFocusTo) && (trapFocus || inside);
      // One under another hands the focus back only if it held it.
      if (!wasTopmost) {
        if (handBack && inside) backTo()?.focus?.();
        return;
      }
      // The one now on top, if it has never been, takes the focus where this
      // one would have handed it back, and keeps that place for its own close.
      const next = topmost();
      if (next && !next.arrived && next.arrive(backTo())) return;
      if (handBack) backTo()?.focus?.();
    };
  }, [isOpen, closeOnEscape, lockBackgroundScroll, trapFocus, moveFocus, returnFocusTo]);

  return ref;
}
